import type { EntityKind } from "@/components/entity-preview/entity-kinds";
import type { IconName } from "@/components/icons/Icon";
import {
  logProjectNavRecent,
  useProjectNavHistoryEntries,
} from "@/components/navigation/project-nav-history";
import { getProjectNavigationRouteKey } from "@/components/navigation/project-navigation-route-key";
import { isQueryWorksheetNode } from "@/components/query/sql/worksheets/worksheet-files";
import {
  NotebookDocumentSortColumn,
  NotebookDocumentSortOrder,
} from "@/gen_src/proto/chalk/notebook/v1/document_service_pb";
import { listNotebookDocuments } from "@/gen_src/proto/chalk/notebook/v1/document_service-NotebookDocumentService_connectquery";
import {
  listWorksheetNodes,
  listWorksheetSpaces,
} from "@/gen_src/proto/chalk/server/v1/worksheets-WorksheetsService_connectquery";
import type { WorksheetNode } from "@/gen_src/proto/chalk/server/v1/worksheets_pb";
import { QueryParam } from "@/utils/hooks/url/use-query-state";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { Routes } from "@/utils/routes";
import { timestampDate, type Timestamp } from "@bufbuild/protobuf/wkt";
import { createQueryOptions, useQuery, useTransport } from "@connectrpc/connect-query";
import { useQueries } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useMemo } from "react";

export const EXPLORE_RECENTS_LIMIT = 10;
// The sidebar mounts these queries each time a hover panel opens; the lists can be this old
// before a mount refetches them, which otherwise disturbs pages showing the same data.
const EXPLORE_RECENTS_STALE_TIME_MS = 30_000;

export interface ExploreRecent {
  /** Identifies the destination across sources: a notebook edited and visited is one entry. */
  key: string;
  name: string;
  href: string;
  icon: IconName;
  /** The object behind the entry, for its preview; pages no kind recognizes are `page`. */
  entity: { kind: EntityKind; id: string };
  /** Whether the latest activity was the reader's visit or an edit to the object. */
  activity: "edited" | "visited";
  /** The latest edit or visit, in epoch milliseconds; 0 when the source reports none. */
  activityAtMs: number;
}

export const getTimestampMs = (timestamp: Timestamp | undefined): number =>
  timestamp == null ? 0 : timestampDate(timestamp).getTime();

/**
 * Folds entries that share a key into one, dated by its latest activity and named by its first
 * source, then orders newest first. Callers list edit sources ahead of visits, so a notebook keeps
 * its saved title over whatever label its page had when it was visited.
 */
export const mergeExploreRecents = (
  recents: readonly ExploreRecent[],
  limit: number,
): ExploreRecent[] => {
  const byKey = new Map<string, { recent: ExploreRecent; order: number }>();
  recents.forEach((recent, order) => {
    const existing = byKey.get(recent.key);
    if (existing == null) {
      byKey.set(recent.key, { recent, order });
      return;
    }
    if (recent.activityAtMs > existing.recent.activityAtMs) {
      existing.recent = {
        ...existing.recent,
        activity: recent.activity,
        activityAtMs: recent.activityAtMs,
      };
    }
  });

  return [...byKey.values()]
    .sort((a, b) => b.recent.activityAtMs - a.recent.activityAtMs || a.order - b.order)
    .slice(0, limit)
    .map(({ recent }) => recent);
};

// Module-level so useQueries keeps the combined result stable until a space's nodes change.
const combineWorksheetNodes = (results: readonly { data?: { nodes: WorksheetNode[] } }[]) =>
  results.map((result) => result.data?.nodes);

/**
 * Recently edited SQL worksheets and notebooks merged with recently visited detail pages: newest
 * activity first, without the page currently open or anything in `excludeKeys`. Worksheets and
 * notebooks are fetched only when their own SQL and Notebooks rows are in the sidebar, so a hidden
 * SQL or Notebooks row stays unqueried.
 */
export const useExploreRecents = ({
  enabled,
  includeWorksheets,
  includeNotebooks,
  excludeKeys,
}: {
  enabled: boolean;
  includeWorksheets: boolean;
  includeNotebooks: boolean;
  excludeKeys: ReadonlySet<string>;
}): ExploreRecent[] => {
  const router = useRouter();
  const { projectId, environmentId } = useRouteAndTeamInfo();
  const transport = useTransport();
  const ready = enabled && projectId != null && environmentId != null;
  const fetchWorksheets = ready && includeWorksheets;
  const fetchNotebooks = ready && includeNotebooks;

  const { data: spacesData } = useQuery(
    listWorksheetSpaces,
    {},
    { enabled: fetchWorksheets, staleTime: EXPLORE_RECENTS_STALE_TIME_MS },
  );
  const spaces = fetchWorksheets ? spacesData?.spaces ?? [] : [];
  const worksheetNodes = useQueries({
    queries: spaces.map((space) => ({
      ...createQueryOptions(
        listWorksheetNodes,
        { spaceId: space.id, includeArchived: false },
        { transport },
      ),
      staleTime: EXPLORE_RECENTS_STALE_TIME_MS,
    })),
    combine: combineWorksheetNodes,
  });
  const { data: notebooksData } = useQuery(
    listNotebookDocuments,
    {
      cursor: "",
      limit: EXPLORE_RECENTS_LIMIT,
      sortColumn: NotebookDocumentSortColumn.UPDATED_AT,
      sortOrder: NotebookDocumentSortOrder.DESC,
    },
    { enabled: fetchNotebooks, staleTime: EXPLORE_RECENTS_STALE_TIME_MS },
  );
  const visits = useProjectNavHistoryEntries();
  const currentRouteKey = getProjectNavigationRouteKey(router.asPath);

  return useMemo(() => {
    if (!ready || projectId == null || environmentId == null) return [];

    const worksheets = fetchWorksheets
      ? worksheetNodes.flatMap((nodes) =>
          (nodes ?? []).filter(isQueryWorksheetNode).map(
            (node): ExploreRecent => ({
              key: `worksheet:${node.id}`,
              name: node.name || "Untitled worksheet",
              href: `${Routes.project.environment.dataExplorerSql(projectId, environmentId)}?${
                QueryParam.WORKSHEET_ID
              }=${encodeURIComponent(node.id)}`,
              icon: "squareAsterisk",
              entity: { kind: "worksheet", id: node.id },
              activity: "edited",
              activityAtMs: getTimestampMs(node.updatedAt),
            }),
          ),
        )
      : [];
    const notebooks = fetchNotebooks
      ? (notebooksData?.documents ?? []).map((document): ExploreRecent => {
          const href = Routes.project.environment.notebook(projectId, environmentId, document.id);
          return {
            key: getProjectNavigationRouteKey(href),
            name: document.title || "Untitled notebook",
            href,
            icon: "notebook",
            entity: { kind: "notebook", id: document.id },
            activity: "edited",
            activityAtMs: getTimestampMs(document.updatedAt),
          };
        })
      : [];
    const visited = visits.map(
      (visit): ExploreRecent => ({
        key: visit.routeKey,
        name: visit.label,
        href: visit.href,
        icon: visit.icon ?? "history",
        entity: visit.entity ?? { kind: "page", id: visit.routeKey },
        activity: "visited",
        activityAtMs: visit.visitedAtMs,
      }),
    );

    logProjectNavRecent("recent sources", {
      worksheets: worksheets.length,
      notebooks: notebooks.length,
      visits: visited.map((visit) => visit.key),
      currentRouteKey,
      excluded: [...excludeKeys],
    });
    return mergeExploreRecents(
      [...worksheets, ...notebooks, ...visited].filter(
        (recent) => recent.key !== currentRouteKey && !excludeKeys.has(recent.key),
      ),
      EXPLORE_RECENTS_LIMIT,
    );
  }, [
    currentRouteKey,
    environmentId,
    excludeKeys,
    fetchNotebooks,
    fetchWorksheets,
    notebooksData,
    projectId,
    ready,
    visits,
    worksheetNodes,
  ]);
};
